<template>
  <base-header class="pb-6 pb-8 pt-8 pt-md-5">
    <!-- Card stats -->
  </base-header>
  <div class="container-fluid mt--7">
    <div class="col-xl-12 order-xl-1">
  <div class="card shadow">
    <div class="card-header bg-transparent text-start">
      <h3 class="mb-0 float-start">{{ $t("Statistics for") }} {{ moment().year() }}</h3>
      <input
        v-model="searchQuery"
        type="text"
        class="form-control float-end"
        style="width: 250px"
        :placeholder="$t('Search by employee name')"
      />
    </div>
    <div class="table-responsive">
      <table class="countStats table align-items-center table-flush">
        <thead class="thead-light">
        <tr>
          <th v-for="column in statisticColumns" :key="column.key" :class="column.key === 'employeeName' ? 'text-start' : ''" scope="col" :aria-sort="ariaSort(column.key)">
            <button type="button" class="statistics-sort" :class="{ active: sortKey === column.key }" @click="sortBy(column.key)">
              {{ $t(column.label) }}
              <i :class="sortIcon(column.key)" aria-hidden="true"></i>
            </button>
          </th>
          <th scope="col">{{ $t("Actions") }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="loading">
          <td colspan="6" class="text-center">{{ $t("Loading...") }}</td>
        </tr>
        <tr v-else-if="filteredStatistics.length === 0">
          <td colspan="6" class="text-center">{{ $t("No statistics available") }}</td>
        </tr>
        <tr v-for="(stat, index) in sortedStatistics" v-else :key="stat.claimedBy || index">
          <th class="text-start"> <router-link :to="`/employees/${stat.claimedBy}`">{{ stat.employeeName }}</router-link></th>
          <td>{{ stat.claimedCount }}</td>
          <td>{{ stat.finishedCount }}</td>
          <td>{{ stat.canceledCount }}</td>
          <td>{{ stat.abandonedCount }}</td>
          <th class="text-start">
            <div class="btn-group" role="group">
            <router-link class="btn btn-primary btn-sm border-0 me-2 text-white" :to="`/employees/${stat.claimedBy}`">View</router-link>
            <router-link class="btn btn-primary btn-sm border-0 me-2 text-white" :to="`/employees/${stat.claimedBy}/edit`">Edit</router-link>
            </div>
        </th>
        </tr>
        </tbody>
        <tfoot>
        <tr>
          <th scope="row">{{ $t("Total") }}</th>
          <td><strong>{{ calculateTotal('claimedCount', sortedStatistics) }}</strong></td>
          <td><strong>{{ calculateTotal('finishedCount', sortedStatistics) }}</strong></td>
          <td><strong>{{ calculateTotal('canceledCount', sortedStatistics) }}</strong></td>
          <td><strong>{{ calculateTotal('abandonedCount', sortedStatistics) }}</strong></td>
          <td></td>
        </tr>
        </tfoot>
      </table>
    </div>
  </div>
    </div>
  </div>
</template>

<script>
import FirebaseFunctionsHelper from "@/helpers/firebaseFunctionsHelper"
import BaseHeader from "@/components/BaseHeader.vue"
import moment from "moment"

export default {
  name: "EmployeeStatistics",
  components: {BaseHeader},
  props: {
    employeeData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      statistics: [],
      loading: true,
      searchQuery: "",
      sortKey: "employeeName",
      sortDirection: "asc",
      statisticColumns: [
        { key: "employeeName", label: "Partner" },
        { key: "claimedCount", label: "Claimed" },
        { key: "finishedCount", label: "Finished" },
        { key: "canceledCount", label: "Canceled" },
        { key: "abandonedCount", label: "Abandoned" }
      ]
    }
  },
  computed: {
    moment() {
      return moment
    },
    filteredStatistics() {
      if (!this.searchQuery.trim()) {
        return this.statistics
      }
      // Case-insensitive string searching
      const query = this.searchQuery.toLowerCase()
      return this.statistics.filter(stat =>
        stat.employeeName &&
        stat.employeeName.toLowerCase().includes(query)
      )
    },
    sortedStatistics() {
      return [...this.filteredStatistics].sort((left, right) => {
        const a = left[this.sortKey]
        const b = right[this.sortKey]
        const aValue = typeof a === "string" ? a.toLocaleLowerCase() : (a ?? 0)
        const bValue = typeof b === "string" ? b.toLocaleLowerCase() : (b ?? 0)
        if (aValue === bValue) return 0
        const result = aValue > bValue ? 1 : -1
        return this.sortDirection === "asc" ? result : -result
      })
    },
  },
  mounted() {
    this.fetchStatistics()
  },
  methods: {
    async fetchStatistics() {
      try {
        this.loading = true

        if (!this.$store.getters.isPartnerCountsCacheExpired) {
          this.statistics = this.$store.state.partnerCountsCache
          return
        }
        const result = await FirebaseFunctionsHelper.callFunction(
            "request-getPartnersCount",
            {}
        )

        if (result && Array.isArray(result)) {
          await this.$store.dispatch("setPartnerCountsCache", result)
          this.statistics = result
        } else {
          this.statistics = []
          console.error("Invalid statistics data format", result)
        }
      } catch (error) {
        console.error("Error fetching statistics", error)
        this.statistics = []
      } finally {
        this.loading = false
      }
    },
    calculateTotal(field, data = null) {
      const arr = data || this.statistics
      return arr.reduce((sum, stat) => sum + (stat[field] || 0), 0)
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc"
      } else {
        this.sortKey = key
        this.sortDirection = "asc"
      }
    },
    ariaSort(key) {
      if (this.sortKey !== key) return "none"
      return this.sortDirection === "asc" ? "ascending" : "descending"
    },
    sortIcon(key) {
      if (this.sortKey !== key) return "fa fa-sort"
      return this.sortDirection === "asc" ? "fa fa-sort-up" : "fa fa-sort-down"
    },
  }
}
</script>

<style scoped>
.table th, .table td {
  white-space: nowrap;
  vertical-align: middle;
}

.countStats th, .countStats td {
  font-size: 13px !important;
}

.statistics-sort {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  text-transform: inherit;
}

.statistics-sort i {
  opacity: .45;
  font-size: 11px;
}

.statistics-sort.active i {
  opacity: 1;
  color: #046380;
}

.statistics-sort:focus-visible {
  outline: 2px solid #046380;
  outline-offset: 4px;
  border-radius: 3px;
}
</style>
